import { Flex, Icon, Text } from '@chakra-ui/react'
import {
  Ban,
  ContactRound,
  MessageSquareText,
  PhoneCall,
  Printer,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useBlockedNumbers,
  useCanBlockNumber,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { CallTarget } from '~/pages/Calls/data/CallDetailData'
import { ContactDetails } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

interface ContactInfoCardProps {
  callTarget: CallTarget
  title: string
  subtitle: string
  contact?: ContactDetails
  number: string
  isExtension?: boolean
  location?: string
  locationIsTitle?: boolean
}
export const ContactInfoCard = ({
  callTarget,
  title,
  subtitle,
  contact,
  number,
  isExtension,
  location,
  locationIsTitle,
}: ContactInfoCardProps) => {
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)
  const showTexting = useShowTexting()

  const { blockedNumbers, unblockNumber, blockNumberWithConfirmation } =
    useBlockedNumbers()
  const canBlockNumber = useCanBlockNumber(number)
  const blockedNumber = useFindBlockedNumber(blockedNumbers, number)

  const {
    callModal: [, setCallModalState],
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
  } = useModalState()

  const handleCallNumber = () => {
    const callee =
      contact !== undefined && contact.defaultNumber !== undefined
        ? { contactNumber: contact.defaultNumber, contact: contact }
        : number
    setCallModalState({
      open: true,
      callee: callee,
    })
  }
  const handleMessageNumber = () => {
    setMessagingModalState({ open: true, number })
  }
  const handleSendFax = () => {
    setFaxModalState({ ...faxModalState, open: true, number })
  }

  const blockNumberButton = useMemo(() => {
    if (blockedNumber) {
      return (
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_100}
          size={Size.sm}
          actionIcon={Ban}
          tooltipText={t('contactInfoCard.tooltip.unblockNumber')}
          onClick={() =>
            unblockNumber(blockedNumber.id, blockedNumber.phoneNumber)
          }
        />
      )
    } else if (canBlockNumber) {
      return (
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_100}
          size={Size.sm}
          actionIcon={Ban}
          tooltipText={t('contactInfoCard.tooltip.blockNumber')}
          onClick={() => blockNumberWithConfirmation(number)}
        />
      )
    }
    return null
  }, [blockedNumber, canBlockNumber, number, t])

  return (
    <Flex
      data-testid="contact-info-card"
      flexDirection="column"
      alignSelf="stretch"
      alignItems="flex-start"
      gap="0.25rem"
      flex="1 0 0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Flex
        data-testid="contact-info-card-header"
        height="1.75rem"
        justifyContent="space-between"
        alignItems="center"
        alignSelf="stretch"
      >
        <Text {...textStyles[textStyleKeys.Body1_Bold]}>
          {t(`contactInfoCard.callTarget.${callTarget}`)}
        </Text>
        <Flex
          data-testid="contact-info-card-actions"
          gap="0.5rem"
          opacity={isHovered ? '100%' : '0'}
          css={{
            '&:has(:focus-visible)': { opacity: '100%' },
            '@media (hover: none)': { opacity: '100%' },
          }}
        >
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_100}
            size={Size.sm}
            actionIcon={PhoneCall}
            tooltipText={t('contactInfoCard.tooltip.call')}
            onClick={handleCallNumber}
          />
          {showTexting && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={MessageSquareText}
              tooltipText={t('contactInfoCard.tooltip.message')}
              onClick={handleMessageNumber}
            />
          )}
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_100}
            size={Size.sm}
            actionIcon={Printer}
            tooltipText={t('contactInfoCard.tooltip.sendFax')}
            onClick={handleSendFax}
          />
          {blockNumberButton}
        </Flex>
      </Flex>
      <Flex
        data-testid="contact-info-card-content"
        flexDirection="column"
        alignItems="flex-start"
        gap="0.25rem"
        alignSelf="stretch"
      >
        <Flex
          data-testid="contact-info-card-row"
          alignItems="center"
          gap="0.25rem"
          alignSelf="stretch"
        >
          {contact?.id && (
            <Icon>
              <ContactRound size={16} />
            </Icon>
          )}
          <Text {...textStyles[textStyleKeys.Body1]} lineClamp={1}>
            {title}
          </Text>
        </Flex>
        <Flex
          data-testid="contact-info-card-row"
          alignItems="center"
          gap="0.25rem"
          alignSelf="stretch"
        >
          {isExtension && (
            <ExtensionBadge
              extension={number}
              variant={ExtensionBadgeVariant.LightGray}
              size={Size.sm}
            />
          )}
          <Text {...textStyles[textStyleKeys.Body1]} lineClamp={1}>
            {subtitle}
          </Text>
        </Flex>
        {!locationIsTitle && (
          <Flex
            data-testid="contact-info-card-row"
            alignItems="center"
            gap="0.25rem"
            alignSelf="stretch"
          >
            <Text {...textStyles[textStyleKeys.Body1]} lineClamp={1}>
              {location}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
